'use client'

import { cn } from 'lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

// Defer video/stream content until in viewport to avoid blocking initial page load
function useInViewport(ref: React.RefObject<HTMLElement | null>, rootMargin = '100px') {
    const [isInView, setIsInView] = useState(false)
    useEffect(() => {
        const el = ref.current
        if (!el) return
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry?.isIntersecting) setIsInView(true)
            },
            { rootMargin, threshold: 0.01 }
        )
        observer.observe(el)
        return () => observer.disconnect()
    }, [ref, rootMargin])
    return isInView
}
import { PolygonWidgetWrapper } from 'components/WidgetWrapper'
import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious
} from 'components/ui/carousel'
import { IconNavigateBack, IconNavigateForward } from 'components/icons'
import { IoPlayCircle } from 'react-icons/io5'
import { BsThreeDotsVertical } from 'react-icons/bs'
import Image from 'next/image'
import AutoScroll from 'embla-carousel-auto-scroll'
import { Button } from 'components/ui/button'

type YouTubeVideo = {
    id: string
    title: string
    thumbnail: string
    publishedAt: string
    channelTitle?: string
}

type TwitchVideo = {
    id: string
    title: string
    thumbnail: string
    publishedAt: string
    channelTitle?: string
    duration?: string
    viewCount?: number
}

const DEFAULT_VIDEO_LIMIT = 3
const MAX_VIDEO_LIMIT = 15

const sanitizeVideoLimit = (value?: number) => {
    if (!value || Number.isNaN(value)) return DEFAULT_VIDEO_LIMIT
    return Math.min(Math.max(value, 1), MAX_VIDEO_LIMIT)
}

const extractYouTubeIdentifier = (value?: string) => {
    if (!value) return ''
    const trimmed = value.trim()
    if (!trimmed) return ''

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        try {
            const url = new URL(trimmed)
            const segments = url.pathname.split('/').filter(Boolean)
            if (segments[0] === 'channel' && segments[1]) return segments[1]
            if (segments[0] === 'c' && segments[1]) return segments[1]
            if (segments[0] === 'user' && segments[1]) return segments[1]
            if (segments[0]?.startsWith('@')) return segments[0]
        } catch {
            return trimmed
        }
    }

    return trimmed
}

// Extract Twitch username from URL or return as-is if already a username
const extractTwitchUsername = (value?: string) => {
    if (!value) return ''
    const trimmed = value.trim()
    if (!trimmed) return ''

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        try {
            const url = new URL(trimmed)
            // Handle twitch.tv URLs: https://www.twitch.tv/username
            if (url.hostname.includes('twitch.tv')) {
                const segments = url.pathname.split('/').filter(Boolean)
                // First segment after domain is usually the username
                if (segments[0]) return segments[0]
            }
        } catch {
            return trimmed
        }
    }

    // Remove @ if present
    if (trimmed.startsWith('@')) {
        return trimmed.slice(1)
    }

    return trimmed
}

// Extract video ID from YouTube URL
const extractVideoIdFromUrl = (url: string): string => {
    try {
        const urlObj = new URL(url)
        if (urlObj.hostname.includes('youtube.com') || urlObj.hostname.includes('youtu.be')) {
            if (urlObj.pathname.includes('/watch')) {
                return urlObj.searchParams.get('v') || ''
            }
            if (urlObj.pathname.includes('/v/')) {
                return urlObj.pathname.split('/v/')[1]?.split('?')[0] || ''
            }
            if (urlObj.hostname.includes('youtu.be')) {
                return urlObj.pathname.slice(1) || ''
            }
        }
    } catch {
        // If URL parsing fails, try to extract from string
        const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/)
        return match ? match[1] : ''
    }
    return ''
}

// Parse RSS feed XML and convert to YouTubeVideo array
const parseRSSFeed = (xmlText: string, maxResults: number): YouTubeVideo[] => {
    try {
        const parser = new DOMParser()
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
        const entries = xmlDoc.querySelectorAll('entry')
        const videos: YouTubeVideo[] = []

        entries.forEach((entry, index) => {
            if (index >= maxResults) return

            const videoId = extractVideoIdFromUrl(entry.querySelector('link')?.getAttribute('href') || '')
            if (!videoId) return

            const title = entry.querySelector('title')?.textContent || 'Untitled video'
            const published =
                entry.querySelector('published')?.textContent || entry.querySelector('updated')?.textContent || ''

            // Get thumbnail from media:thumbnail or media:group
            let thumbnail = ''
            const mediaThumbnail = entry.querySelector('media\\:thumbnail, thumbnail')
            if (mediaThumbnail) {
                thumbnail = mediaThumbnail.getAttribute('url') || ''
            } else {
                // Fallback: construct thumbnail URL from video ID
                thumbnail = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
            }

            const author = entry.querySelector('author name')?.textContent || ''
            const channelTitle = author || undefined

            videos.push({
                id: videoId,
                title,
                thumbnail,
                publishedAt: published,
                channelTitle
            })
        })

        return videos
    } catch (error) {
        console.error('Error parsing RSS feed:', error)
        return []
    }
}

// PolygonVideoCard component
const PolygonVideoCard = ({
    video,
    onSelect,
    isActive,
    autoPlayParam,
    platform = 'youtube'
}: {
    video: YouTubeVideo | TwitchVideo
    onSelect: (videoId: string) => void
    isActive: boolean
    autoPlayParam: string
    platform?: 'youtube' | 'twitch'
}) => {
    const handleClick = () => {
        if (!isActive) {
            onSelect(video.id)
        }
    }

    return (
        <div
            onClick={handleClick}
            className='relative md:w-[380px] md:max-w-[380px] extra_large:w-[500px] extra_large:max-w-[500px] grid p-0.5 gap-4 md:h-[300px] h-[200px] w-[200px] extra_large:h-[400px] cursor-pointer'
        >
            <svg
                className='absolute inset-0 w-full h-full -z-10 pointer-events-none'
                viewBox='0 0 100 100'
                preserveAspectRatio='none'
                xmlns='http://www.w3.org/2000/svg'
                aria-hidden='true'
            >
                {/* faded background */}
                <polygon points='6,0 100,0 100,94 94,100 0,100 0,6' fill='currentColor' fillOpacity='0.06' />
                {/* border */}
                <polygon
                    points='6,0 100,0 100,94 94,100 0,100 0,6'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth='1'
                    strokeLinejoin='bevel'
                    vectorEffect='non-scaling-stroke'
                />
            </svg>

            <div className='flex flex-col z-10 gap-2 absolute bottom-2 px-4 w-full min-w-0'>
                <div className='flex justify-between w-full min-w-0 items-center gap-2'>
                    <p className='truncate font-bold text-foreground'>{video.title}</p>
                    <BsThreeDotsVertical className='size-5 shrink-0 text-foreground' />
                </div>
                {video.channelTitle && <p className='truncate text-foreground'>{video.channelTitle}</p>}
            </div>

            {!isActive && (
                <IoPlayCircle className='md:size-20 size-12 text-accent-foreground z-10 absolute inset-0 m-auto' />
            )}
            <div className='[clip-path:polygon(6%_0,100%_0,100%_94%,94%_100%,0_100%,0_6%)] size-full flex flex-col'>
                {isActive ? (
                    platform === 'twitch' ? (
                        <iframe
                            src={`https://player.twitch.tv/?video=${video.id}&parent=${typeof window !== 'undefined' ? window.location.hostname : ''}&autoplay=${autoPlayParam === '1'}`}
                            className='w-full h-full border-0'
                            allowFullScreen
                            allow='autoplay; fullscreen; picture-in-picture'
                        />
                    ) : (
                        <iframe
                            src={`https://www.youtube.com/embed/${video.id}?autoplay=${autoPlayParam}`}
                            className='w-full h-full border-0'
                            allowFullScreen
                            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                        />
                    )
                ) : (
                    <div className='relative size-full'>
                        <Image
                            src={video.thumbnail || '/placeholder-video.jpg'}
                            alt={video.title}
                            fill
                            className='object-cover opacity-40'
                            sizes='(max-width: 768px) 200px, (max-width: 1280px) 380px, 500px'
                        />
                    </div>
                )}
            </div>
        </div>
    )
}

interface StreamWidgetProps {
    data: IStreamWidget
    className?: string
    config?: IDomainConfig
}

// Kick component using Kick's embed API
const KickEmbed = ({ channelId = 'xqc', showChat, autoPlay }: Omit<IStreamWidget, 'platform'>) => {
    const kickContainerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (kickContainerRef.current) {
            const layout = showChat ? 'video-and-chat' : 'video'
            const muted = autoPlay ? 'true' : 'false'

            kickContainerRef.current.innerHTML = `
                <iframe
                    src="https://player.kick.com/${channelId}?layout=${layout}&muted=${muted}&autoplay=${autoPlay ? 'true' : 'false'}"
                    width="100%"
                    height="100%"
                    style="aspect-ratio: 16/9; border: none;"
                    allowfullscreen
                    allow="autoplay; fullscreen; picture-in-picture"
                ></iframe>
            `
        }
    }, [channelId, showChat, autoPlay])

    return <div ref={kickContainerRef} className='w-full aspect-video' />
}

// Twitch component with multiple videos support
const TwitchEmbed = ({
    channelId = 'runthefutmarket',
    showChat,
    autoPlay,
    contentSource = 'direct',
    maxVideos
}: Omit<IStreamWidget, 'platform'>) => {
    const [isClient, setIsClient] = useState(false)
    const [videos, setVideos] = useState<TwitchVideo[]>([])
    const [activeVideoId, setActiveVideoId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [carouselApi, setCarouselApi] = useState<CarouselApi | undefined>(undefined)
    const [mobileShowChat, setMobileShowChat] = useState(false)

    useEffect(() => {
        setIsClient(true)
    }, [])

    const autoPlayParam = autoPlay ? '1' : '0'
    // Extract username from channelId (handles URLs like https://twitch.tv/username or just "username")
    // Compute outside useEffect for use in render functions
    const extractedUsername = extractTwitchUsername(channelId)
    const trimmedChannelId = extractedUsername || ''
    const isDirectSource = contentSource === 'direct'

    useEffect(() => {
        // Recompute values inside useEffect to ensure they're fresh
        const trimmedChannelId = extractedUsername || ''
        const isDirectSource = contentSource === 'direct'

        if (isDirectSource) {
            setVideos([])
            setActiveVideoId(null)
            setErrorMessage(null)
            setIsLoading(false)
            return
        }

        if (!trimmedChannelId) {
            setErrorMessage('Provide a Twitch username to load videos.')
            setVideos([])
            setActiveVideoId(null)
            return
        }

        let cancel = false
        const controller = new AbortController()

        const fetchVideos = async () => {
            setIsLoading(true)
            setErrorMessage(null)

            try {
                const videoLimit = sanitizeVideoLimit(maxVideos)
                const apiUrl = `/api/twitch-videos?username=${encodeURIComponent(trimmedChannelId)}&max=${videoLimit}&contentSource=${encodeURIComponent(contentSource || 'recent')}`

                const response = await fetch(apiUrl, { signal: controller.signal })

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({ error: 'Unknown error' }))
                    throw new Error(
                        errorData.error || errorData.details || `Unable to load videos from Twitch (${response.status})`
                    )
                }

                const data = await response.json()

                let parsedVideos = (data.videos || []) as TwitchVideo[]

                // Filter based on contentSource
                if (contentSource === 'recent') {
                    parsedVideos = parsedVideos.slice(0, videoLimit)
                } else if (contentSource === 'live') {
                    // For live, we'll just take the first video (most recent)
                    parsedVideos = parsedVideos.slice(0, 1)
                }

                if (!cancel) {
                    setVideos(parsedVideos)
                    setActiveVideoId(parsedVideos[0]?.id ?? null)
                }
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return
                }

                if (!cancel) {
                    const errorMsg = error instanceof Error ? error.message : 'Unable to load videos.'
                    setErrorMessage(errorMsg)
                    setVideos([])
                    setActiveVideoId(null)
                }
            } finally {
                if (!cancel) setIsLoading(false)
            }
        }

        fetchVideos()

        return () => {
            cancel = true
            controller.abort()
        }
    }, [contentSource, maxVideos, channelId])

    useEffect(() => {
        if (!isDirectSource && videos.length && !activeVideoId) {
            setActiveVideoId(videos[0].id)
        }
    }, [videos, isDirectSource, activeVideoId])

    const handleSelectVideo = (videoId: string) => {
        setActiveVideoId(videoId)
    }

    const resetAutoScroll = useCallback(() => {
        if (carouselApi) {
            try {
                const plugins = carouselApi.plugins()
                const pluginApi = plugins?.autoScroll
                if (pluginApi && typeof pluginApi === 'object' && 'stop' in pluginApi && 'play' in pluginApi) {
                    ;(pluginApi as any).stop()
                    setTimeout(() => (pluginApi as any).play(), 5000)
                }
            } catch (error) {
                console.warn('Error accessing autoScroll plugin:', error)
            }
        }
    }, [carouselApi])

    const scrollPrev = useCallback(() => {
        if (carouselApi) carouselApi.scrollPrev()
        resetAutoScroll()
    }, [carouselApi, resetAutoScroll])

    const scrollNext = useCallback(() => {
        if (carouselApi) carouselApi.scrollNext()
        resetAutoScroll()
    }, [carouselApi, resetAutoScroll])

    const renderDirectEmbed = () => {
        const channelForEmbed = trimmedChannelId || 'runthefutmarket'
        return (
            <>
                <iframe
                    src={`https://player.twitch.tv/?channel=${channelForEmbed}&parent=${isClient ? window.location.hostname : ''}&autoplay=${autoPlayParam === '1'}&muted=false`}
                    width='100%'
                    className='aspect-video border-0 w-full'
                    allowFullScreen
                    allow='autoplay; fullscreen; picture-in-picture'
                />
                {showChat && isClient && (
                    <div className='mt-4 flex flex-col gap-2'>
                        <Button
                            variant='outline'
                            size='sm'
                            className='md:hidden w-full'
                            onClick={() => setMobileShowChat(!mobileShowChat)}
                        >
                            {mobileShowChat ? 'Hide Chat' : 'Show Chat'}
                        </Button>
                        <div className={cn('w-full', !mobileShowChat && 'hidden md:block')}>
                            <iframe
                                src={`https://www.twitch.tv/embed/${channelForEmbed}/chat?parent=${window.location.hostname}`}
                                width='100%'
                                height='400px'
                                className='border-0 w-full h-[300px] md:h-[400px]'
                            />
                        </div>
                    </div>
                )}
            </>
        )
    }

    const renderDynamicEmbed = () => {
        if (isLoading) {
            return (
                <div className='flex items-center justify-center h-64'>
                    <p className='text-foreground'>Loading videos...</p>
                </div>
            )
        }

        if (errorMessage) {
            return (
                <div className='flex items-center justify-center h-64'>
                    <p className='text-red-500'>{errorMessage}</p>
                </div>
            )
        }

        if (!videos.length) {
            return (
                <div className='flex items-center justify-center h-64'>
                    <p className='text-foreground'>No videos available.</p>
                </div>
            )
        }

        return (
            <>
                {/* Video carousel */}
                <div className='grid px-4 md:px-0'>
                    <div className='relative w-full grid overflow-hidden'>
                        <Carousel
                            setApi={setCarouselApi}
                            opts={{
                                align: 'start',
                                loop: true,
                                dragFree: true,
                                containScroll: 'trimSnaps'
                            }}
                            plugins={[
                                AutoScroll({
                                    speed: 1,
                                    stopOnInteraction: true,
                                    playOnInit: true,
                                    direction: 'forward',
                                    rootNode: emblaRoot => emblaRoot.parentElement as HTMLElement
                                })
                            ]}
                            className='w-full relative grid'
                        >
                            <div className='w-full flex justify-between items-center md:mb-6 mb-4 extra_large:mb-8'>
                                <p className='text-2xl md:text-4xl extra_large:text-5xl font-extrabold text-foreground leading-normal'>
                                    Videos
                                </p>

                                <div className='relative right-12 w-[45px] my-4 flex items-center gap-2'>
                                    <CarouselPrevious
                                        onClick={scrollPrev}
                                        className='polygon_shape size-7 md:h-10 md:w-10 ml-5 md:ml-0 flex rounded hover:!bg-primary/[.24] bg-accent-foreground/5  absolute inset-0 mr-auto'
                                        icon={<IconNavigateBack className='shrink-0' />}
                                    />
                                    <CarouselNext
                                        onClick={scrollNext}
                                        className='polygon_shape_reverse size-7 md:h-10 md:w-10 flex rounded hover:!bg-primary/[.24] bg-accent-foreground/5   absolute inset-r-0'
                                        icon={<IconNavigateForward className='shrink-0' />}
                                    />
                                </div>
                            </div>
                            <CarouselContent className='mx-auto'>
                                {videos.map(video => (
                                    <CarouselItem key={video.id} className='md:pl-4 pl-3 basis-1/1'>
                                        <PolygonVideoCard
                                            video={video}
                                            onSelect={handleSelectVideo}
                                            isActive={activeVideoId === video.id}
                                            autoPlayParam={autoPlayParam}
                                            platform='twitch'
                                        />
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                        </Carousel>
                    </div>
                </div>
            </>
        )
    }

    return <div className='w-full h-full'>{isDirectSource ? renderDirectEmbed() : renderDynamicEmbed()}</div>
}

const YouTubeEmbed = ({
    channelId = 'BorasLegend',
    showChat,
    autoPlay,
    contentSource = 'direct',
    maxVideos
}: Omit<IStreamWidget, 'platform'>) => {
    const [isClient, setIsClient] = useState(false)
    const [videos, setVideos] = useState<YouTubeVideo[]>([])
    const [activeVideoId, setActiveVideoId] = useState<string | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const [carouselApi, setCarouselApi] = useState<CarouselApi | undefined>(undefined)
    const [mobileShowChat, setMobileShowChat] = useState(false)

    useEffect(() => {
        setIsClient(true)
    }, [])

    const autoPlayParam = autoPlay ? '1' : '0'
    const trimmedChannelId = channelId?.trim() || ''
    const directIdentifier = extractYouTubeIdentifier(channelId)
    const isDirectVideoId = directIdentifier.length === 11 && !directIdentifier.startsWith('UC')
    const isDirectSource = contentSource === 'direct'
    const needsChannelId = contentSource === 'recent' || contentSource === 'live'

    const chatUrl =
        isClient && showChat
            ? isDirectSource
                ? isDirectVideoId
                    ? `https://www.youtube.com/live_chat?v=${directIdentifier || 'BorasLegend'}&embed_domain=${window.location.hostname}`
                    : `https://www.youtube.com/live_chat?channel=${directIdentifier || 'BorasLegend'}&embed_domain=${window.location.hostname}`
                : activeVideoId
                  ? `https://www.youtube.com/live_chat?v=${activeVideoId}&embed_domain=${window.location.hostname}`
                  : ''
            : ''

    useEffect(() => {
        // Use values computed from props to ensure they're fresh

        if (isDirectSource) {
            setVideos([])
            setActiveVideoId(null)
            setErrorMessage(null)
            setIsLoading(false)
            return
        }

        if (!trimmedChannelId) {
            setErrorMessage('Provide a YouTube channel ID to load videos.')
            setVideos([])
            setActiveVideoId(null)
            return
        }

        // Only require channel ID starting with 'UC' for recent or live content
        if (needsChannelId && !trimmedChannelId.startsWith('UC')) {
            setErrorMessage("Live or recent videos require the channel's ID (starts with 'UC').")
            setVideos([])
            setActiveVideoId(null)
            return
        }

        let cancel = false
        const controller = new AbortController()

        const fetchVideos = async () => {
            setIsLoading(true)
            setErrorMessage(null)

            try {
                const videoLimit = sanitizeVideoLimit(maxVideos)

                // Use RSS feed via API proxy to avoid CORS issues
                const rssUrl = `/api/youtube-rss?channel_id=${trimmedChannelId}`

                const response = await fetch(rssUrl, { signal: controller.signal })
                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error')
                    console.error('[YouTube] API error:', response.status, errorText)
                    throw new Error(`Unable to load videos from YouTube RSS feed (${response.status}).`)
                }

                const xmlText = await response.text()
                let parsedVideos = parseRSSFeed(xmlText, videoLimit)

                // Filter out live streams if contentSource is 'recent'
                if (contentSource === 'recent') {
                    // RSS feed doesn't indicate live status, so we'll just take the most recent videos
                    parsedVideos = parsedVideos.slice(0, videoLimit)
                } else if (contentSource === 'live') {
                    // For live, we'll just take the first video (most recent)
                    parsedVideos = parsedVideos.slice(0, 1)
                }

                if (!cancel) {
                    setVideos(parsedVideos)
                    setActiveVideoId(parsedVideos[0]?.id ?? null)
                }
            } catch (error) {
                if (error instanceof DOMException && error.name === 'AbortError') {
                    return
                }

                console.error('[YouTube] Widget error:', error)
                if (!cancel) {
                    const errorMsg = error instanceof Error ? error.message : 'Unable to load videos.'
                    setErrorMessage(errorMsg)
                    setVideos([])
                    setActiveVideoId(null)
                    console.error('[YouTube] Error state set:', errorMsg)
                }
            } finally {
                if (!cancel) setIsLoading(false)
            }
        }

        fetchVideos()

        return () => {
            cancel = true
            controller.abort()
        }
    }, [contentSource, maxVideos, channelId])

    useEffect(() => {
        if (!isDirectSource && videos.length && !activeVideoId) {
            setActiveVideoId(videos[0].id)
        }
    }, [videos, isDirectSource, activeVideoId])

    const handleSelectVideo = (videoId: string) => {
        setActiveVideoId(videoId)
    }

    const resetAutoScroll = useCallback(() => {
        if (carouselApi) {
            // Stop autoscroll
            try {
                const plugins = carouselApi.plugins()
                const pluginApi = plugins?.autoScroll
                if (pluginApi && typeof pluginApi === 'object' && 'stop' in pluginApi && 'play' in pluginApi) {
                    ;(pluginApi as any).stop()
                    // Resume after 5 seconds
                    setTimeout(() => (pluginApi as any).play(), 5000)
                }
            } catch (error) {
                // Silently handle plugin access errors
                console.warn('Error accessing autoScroll plugin:', error)
            }
        }
    }, [carouselApi])

    const scrollPrev = useCallback(() => {
        if (carouselApi) carouselApi.scrollPrev()
        resetAutoScroll()
    }, [carouselApi, resetAutoScroll])

    const scrollNext = useCallback(() => {
        if (carouselApi) carouselApi.scrollNext()
        resetAutoScroll()
    }, [carouselApi, resetAutoScroll])

    const renderDirectEmbed = () => {
        const fallbackId = directIdentifier || 'BorasLegend'
        const directUrl = isDirectVideoId
            ? `https://www.youtube.com/embed/${directIdentifier}?autoplay=${autoPlayParam}`
            : `https://www.youtube.com/embed?channel=${fallbackId}&autoplay=${autoPlayParam}`

        return (
            <>
                <iframe
                    src={directUrl}
                    width='100%'
                    className='aspect-video border-0'
                    allowFullScreen
                    allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                />
                {showChat && isClient && chatUrl && (
                    <div className='mt-4 flex flex-col gap-2'>
                        <Button
                            variant='outline'
                            size='sm'
                            className='md:hidden w-full'
                            onClick={() => setMobileShowChat(!mobileShowChat)}
                        >
                            {mobileShowChat ? 'Hide Chat' : 'Show Chat'}
                        </Button>
                        <div className={cn('w-full', !mobileShowChat && 'hidden md:block')}>
                            <iframe
                                src={chatUrl}
                                width='100%'
                                height='400px'
                                className='border-0 w-full h-[300px] md:h-[400px]'
                            />
                        </div>
                    </div>
                )}
            </>
        )
    }

    const renderDynamicEmbed = () => {
        if (isLoading) {
            return (
                <div className='flex items-center justify-center h-64'>
                    <p className='text-foreground'>Loading videos...</p>
                </div>
            )
        }

        if (errorMessage) {
            return (
                <div className='flex items-center justify-center h-64'>
                    <p className='text-red-500'>{errorMessage}</p>
                </div>
            )
        }

        if (!videos.length) {
            return (
                <div className='flex items-center justify-center h-64'>
                    <p className='text-foreground'>No videos available.</p>
                </div>
            )
        }

        return (
            <>
                {/* Video carousel */}
                <div className='grid px-4 md:px-0'>
                    <div className='relative w-full grid overflow-hidden'>
                        <Carousel
                            setApi={setCarouselApi}
                            opts={{
                                align: 'start',
                                loop: true,
                                dragFree: true,
                                containScroll: 'trimSnaps'
                            }}
                            plugins={[
                                AutoScroll({
                                    speed: 1,
                                    stopOnInteraction: true,
                                    playOnInit: true,
                                    direction: 'forward',
                                    rootNode: emblaRoot => emblaRoot.parentElement as HTMLElement
                                })
                            ]}
                            className='w-full relative grid'
                        >
                            <div className='w-full flex justify-between items-center md:mb-6 mb-4 extra_large:mb-8'>
                                <p className='text-2xl md:text-4xl extra_large:text-5xl font-extrabold text-foreground leading-normal'>
                                    Videos
                                </p>

                                <div className='relative right-12 w-[45px] my-4 flex items-center gap-2'>
                                    <CarouselPrevious
                                        onClick={scrollPrev}
                                        className='polygon_shape size-7 md:h-10 md:w-10 ml-5 md:ml-0 flex rounded hover:!bg-primary/[.24] bg-accent-foreground/5  absolute inset-0 mr-auto'
                                        icon={<IconNavigateBack className='shrink-0' />}
                                    />
                                    <CarouselNext
                                        onClick={scrollNext}
                                        className='polygon_shape_reverse size-7 md:h-10 md:w-10 flex rounded hover:!bg-primary/[.24] bg-accent-foreground/5   absolute inset-r-0'
                                        icon={<IconNavigateForward className='shrink-0' />}
                                    />
                                </div>
                            </div>
                            <CarouselContent className='mx-auto'>
                                {videos.map(video => (
                                    <CarouselItem key={video.id} className='md:pl-4 pl-3 basis-1/1'>
                                        <PolygonVideoCard
                                            video={video}
                                            onSelect={handleSelectVideo}
                                            isActive={activeVideoId === video.id}
                                            autoPlayParam={autoPlayParam}
                                            platform='youtube'
                                        />
                                    </CarouselItem>
                                ))}
                            </CarouselContent>
                        </Carousel>
                    </div>
                </div>
            </>
        )
    }

    return <div className='w-full h-full'>{isDirectSource ? renderDirectEmbed() : renderDynamicEmbed()}</div>
}

export default function StreamWidget({ data, className }: StreamWidgetProps) {
    if (!data) {
        return (
            <PolygonWidgetWrapper className='my-6'>
                <div
                    className={cn(
                        'stream-widget overflow-hidden !h-fit mx-auto justify-center items-center',
                        className
                    )}
                >
                    <div className='md:p-2 p-1'>
                        <div className='flex items-center justify-center h-64'>
                            <p className='text-foreground'>No stream data available.</p>
                        </div>
                    </div>
                </div>
            </PolygonWidgetWrapper>
        )
    }

    if (!data.platform) {
        return (
            <PolygonWidgetWrapper className='my-6'>
                <div
                    className={cn(
                        'stream-widget overflow-hidden !h-fit mx-auto justify-center items-center',
                        className
                    )}
                >
                    <div className='md:p-2 p-1'>
                        <div className='flex items-center justify-center h-64'>
                            <p className='text-foreground'>Platform not configured.</p>
                        </div>
                    </div>
                </div>
            </PolygonWidgetWrapper>
        )
    }

    if (!data.channelId) {
        return (
            <PolygonWidgetWrapper className='my-6'>
                <div
                    className={cn(
                        'stream-widget overflow-hidden !h-fit mx-auto justify-center items-center',
                        className
                    )}
                >
                    <div className='md:p-2 p-1'>
                        <div className='flex items-center justify-center h-64'>
                            <p className='text-foreground'>Channel ID is required.</p>
                        </div>
                    </div>
                </div>
            </PolygonWidgetWrapper>
        )
    }

    const containerRef = useRef<HTMLDivElement>(null)
    const isInView = useInViewport(containerRef)

    return (
        <PolygonWidgetWrapper className='my-6'>
            <div
                ref={containerRef}
                className={cn('stream-widget overflow-hidden !h-fit mx-auto justify-center items-center', className)}
            >
                <div className='md:p-2 p-1'>
                    {!isInView ? (
                        <div className='aspect-video w-full bg-muted/20 animate-pulse rounded-lg flex items-center justify-center min-h-[200px]' />
                    ) : (
                        <>
                            {data.platform === 'kick' && (
                                <KickEmbed
                                    channelId={data.channelId}
                                    showChat={data.showChat}
                                    autoPlay={data.autoPlay}
                                />
                            )}
                            {data.platform === 'twitch' && (
                                <TwitchEmbed
                                    channelId={data.channelId}
                                    showChat={data?.showChat}
                                    autoPlay={data?.autoPlay}
                                    contentSource={data?.contentSource || 'direct'}
                                    maxVideos={data?.maxVideos}
                                />
                            )}
                            {data.platform === 'youtube' && (
                                <YouTubeEmbed
                                    channelId={data.channelId}
                                    showChat={data?.showChat}
                                    autoPlay={data?.autoPlay}
                                    contentSource={data?.contentSource || 'direct'}
                                    maxVideos={data?.maxVideos}
                                />
                            )}
                        </>
                    )}
                </div>
            </div>
        </PolygonWidgetWrapper>
    )
}

// Add TypeScript global declaration for Twitch
declare global {
    interface Window {
        Twitch?: {
            Embed: new (id: string, options: any) => any
        }
    }
}
